import React, {PureComponent} from "react"
import {withRouter, Redirect, RouteProps} from "react-router-dom"
import {connect, Dispatch} from "react-redux"

import intercom from "@irati/utils/intercom"
import {PartialRoute, IndexRedirect} from "@irati/utils/history/routing"
import {TenantOnboarding, UserOnboarding, TenantOnboardingStep, Tenant} from "@irati/types"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import AuthActions from "@irati/auth/store/actions/AuthActions"
import Loading from "@irati/ui/src/Loading"
import {hangPromise} from "@irati/utils/promise"
import OnboardingService from "@irati/onboarding/services/onboarding"
import OnboardingStep from "@irati/onboarding/enums/Step"
import {getTenant} from "@irati/identity/services/tenant"
import authentication from "@irati/auth"
import PerkboxTour, {TourStep} from "@irati-web/tour"

import InviteAdmin from "../components/InviteAdmin"
import {getOrderedSectionSteps, getStepDetails, isValidStep, getFirstIncompleteStep, isPathActive, areAllStepsDone} from "../helpers"
import {StepContent, SideBar, GhostActions} from "../components"
import TenantOnboardingAppContainer from "../containers/TenantOnboarding"
import {FullWidth, FlexContainer} from "../components/styled"
import stepDetails, {SetupSections} from "../constants/stepConfig"
import tenantSetupTourSteps, {TENANT_SETUP_TOUR_PREFIX} from "../tour"

interface Props extends InjectIntl, RouteProps {
  path: string,
  updateAuthTenant: (tenant: Tenant) => void
}

interface State {
  load: Promise<void>,
  tenantOnboarding?: TenantOnboarding,
  tourSteps: TourStep[],
  showTour: boolean,
  tenant?: Tenant
}

const LEGACY_TENANT_TOUR_COOKIE = "tenantSetupTourComplete"
const LEGACY_TOUR_COMPLETE_VALUE = "tourComplete"

class PartialRoutes extends PureComponent<Props, State> {
  state: State = {
    load: hangPromise(),
    tourSteps: tenantSetupTourSteps,
    showTour: false
  }

  componentDidMount() {
    const promises = [
      OnboardingService.getTenantOnboarding(),
      getTenant(authentication.userTenant().uuid),
      this.shouldShowTour()
    ]
    const {updateAuthTenant} = this.props
    const load = Promise.all(promises).then(async(res) => {
      const tenantOnboarding: TenantOnboarding = res[0]
      const tenant = res[1]
      const showTour = res[2]
      const steps = tenantOnboarding.assignedSteps.filter(isValidStep)
      tenantOnboarding.assignedSteps = steps
      if (areAllStepsDone(steps)) {
        await OnboardingService.updateTenantOnboarding({onboardingFinished: true})
        tenantOnboarding.onboardingFinished = true
      }
      await updateAuthTenant(tenant)
      this.setState({tenantOnboarding, tenant})
      // do not show tour on invite admin path as it has no footer
      if (!isPathActive("invite-admin")) {
        this.setState({showTour})
      }
    })
    this.setState({load})
  }

  // if you navigate away from invite-admin path show the tour (as the footer will be there)
  componentWillReceiveProps = (nextProps: Props) => {
    if (nextProps.location?.pathname !== this.props.location?.pathname) {
      if (nextProps.location?.pathname !== "/setup-tenant/invite-admin") {
        this.shouldShowTour().then((showTour) => this.setState({showTour}))
      }
    }
  }

  shouldShowTour = (): Promise<boolean> => {
    if (authentication.isUserGhost()) {
      return Promise.resolve(false)
    }
    // Moved from LocalStorage -> graph (Feb-2020), check kept to avoid double tours
    const showTourLegecyCheck = localStorage.getItem(LEGACY_TENANT_TOUR_COOKIE) === LEGACY_TOUR_COMPLETE_VALUE
    return OnboardingService.get(authentication.user().uuid).then((res: UserOnboarding) => {
      const showTour = !res.completedSteps.includes(OnboardingStep.HAS_COMPLETED_TENANT_SETUP_TOUR)
      // If the service thinks the user should see the tour but they have the LocalStorage value, fire an update
      if(showTour && showTourLegecyCheck) {
        this.neverShowTourAgain()
        return false
      }
      return showTour
    })
  }
  neverShowTourAgain = () =>  OnboardingService.completeStep(authentication.user(), OnboardingStep.HAS_COMPLETED_TENANT_SETUP_TOUR)

  componentWillUnmount = () => intercom.show()

  getDefaultRoute = (steps: TenantOnboardingStep[]) => {
    const {nestedPath, title} = stepDetails[getFirstIncompleteStep(steps).stepId]
    return <IndexRedirect match={this.props.path} to={nestedPath} title={title} />
  }

  updateOnboardingSteps = async(steps: TenantOnboardingStep[]) => {
    const tenantOnboarding = {...this.state.tenantOnboarding}
    if (areAllStepsDone(steps)) {
      await OnboardingService.updateTenantOnboarding({onboardingFinished: true})
      tenantOnboarding.onboardingFinished = true
    }
    tenantOnboarding.assignedSteps = steps.filter(isValidStep)
    this.setState({tenantOnboarding})
  }


  render() {
    const {path, formatMessage} = this.props
    const {tenantOnboarding, load, tourSteps, showTour} = this.state

    if (tenantOnboarding?.onboardingFinished) {
      return <Redirect to="/" />
    }

    return (
      <TenantOnboardingAppContainer>
        <Loading load={load}>
          <PerkboxTour
            tourPrefix={TENANT_SETUP_TOUR_PREFIX}
            run={showTour}
            onClose={this.neverShowTourAgain}
            onComplete={this.neverShowTourAgain}
            steps={tourSteps} />
          {tenantOnboarding && (
            <FlexContainer>
              <SideBar
                onboardingSteps={tenantOnboarding.assignedSteps}
                customiseSteps={getOrderedSectionSteps(tenantOnboarding.assignedSteps, SetupSections.CUSTOMISE)}
                communicateSteps={getOrderedSectionSteps(tenantOnboarding.assignedSteps, SetupSections.COMMUNICATE)}
                inviteSteps={getOrderedSectionSteps(tenantOnboarding.assignedSteps, SetupSections.INVITE)} />
              <FullWidth>
                <PartialRoute component={InviteAdmin} match={path} path="/invite-admin" title="invite-admin" />
                {tenantOnboarding.assignedSteps!.map((step: TenantOnboardingStep) => {
                  const {title, subTitle, subTitleMoreText, nestedPath, stepComponent} = getStepDetails(step)
                  return (
                    <PartialRoute
                      key={step.stepId}
                      component={StepContent}
                      componentProps={{
                        updateOnboardingSteps: this.updateOnboardingSteps,
                        tenantOnboarding,
                        StepComponent: stepComponent,
                        title,
                        subTitle,
                        subTitleMoreText,
                        ...step
                      }}
                      match={path}
                      path={nestedPath}
                      title={formatMessage({id: title})} />
                  )
                })}
              </FullWidth>
              {this.getDefaultRoute(tenantOnboarding.assignedSteps)}
            </FlexContainer>
          )}
        </Loading>
        <GhostActions />
      </TenantOnboardingAppContainer>
    )
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    updateAuthTenant: () => dispatch(AuthActions.updateTenant())
  }
}

export default connect(
  null,
  mapDispatchToProps
)(withRouter(injectIntl(PartialRoutes)))
